// The single place that talks to the doc_live API.
//
// Everything odd about the server's envelope is handled here so no call site
// has to know about it:
//   success        -> { status: "success", data: {...} }        we return `data`
//   login          -> adds a top-level `token` beside `data`    see `raw`
//   getAllGroups   -> { data: { data: [...] } } (double-nested)  see `unwrap`
//   errors         -> { status: "Error", data: { message } }     thrown as ApiError
//
// Errors always arrive as an ApiError carrying `status` (the HTTP code), so
// callers can branch on 401/404 without parsing strings.

const BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/+$/, '');

const TOKEN_KEY = 'dok.token';

export const getToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private browsing / disabled storage: fall back to being logged out
    // rather than crashing the app on boot.
    return null;
  }
};

export const setToken = (token) => {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — the session simply won't survive a refresh */
  }
};

export class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

// Listeners for "the token is no longer good" — AuthContext subscribes so a 401
// anywhere logs the user out instead of leaving a half-broken page.
const unauthorizedHandlers = new Set();
export const onUnauthorized = (fn) => {
  unauthorizedHandlers.add(fn);
  return () => unauthorizedHandlers.delete(fn);
};

/**
 * The message to show a human, from any of the shapes the API uses.
 *
 * `{ status, data: { message } }` is the global handler's shape, but individual
 * controllers disagree: the report and session endpoints put `message` at the top
 * level, `createReport` uses `error`, and the OTP flow puts the SENTENCE in
 * `status` ("You have already requested an OTP"). Without that last case those
 * failures reached the user as "Request failed (HTTP 400)".
 *
 * `status` is only used when it isn't one of the envelope's own words, since
 * "Error" / "fail" / "success" are labels rather than explanations.
 */
const ENVELOPE_WORDS = /^(error|fail|failed|success)$/i;

const errorMessage = (payload, fallback) => {
  if (!payload) return fallback;
  if (typeof payload === 'string') return payload || fallback;
  const status = typeof payload.status === 'string' && !ENVELOPE_WORDS.test(payload.status.trim())
    ? payload.status
    : null;
  return payload?.data?.message || payload?.message
    || payload?.data?.error || payload?.error || status || fallback;
};

/**
 * Does this 401 mean the SESSION is bad, or just that the caller has the wrong
 * ROLE for this endpoint?
 *
 * The API answers 401 for both — studentProtect/adminProtect reject a valid
 * token of the other type with "Not authorized as student|admin" rather than a
 * 403. Treating that as a dead session logged an admin out the moment they
 * landed on a student-only page, which is how this was found.
 */
const isRoleMismatch = (message) => /not authorized as (a|an )?(admin|student|assistant)/i.test(message || '');

/**
 * @param {string} method
 * @param {string} path      e.g. '/login' or '/topic/getAllTopics'
 * @param {object} [options]
 * @param {any}    [options.body]        JSON-serialised for non-GET
 * @param {object} [options.query]       appended as a query string
 * @param {boolean}[options.raw]         resolve the whole payload, not just `data`
 *                                       (login needs it — `token` sits outside `data`)
 * @param {function}[options.isEmpty]    (status, payload) => boolean. When it
 *                                       returns true the response resolves to
 *                                       `emptyValue` instead of throwing.
 *                                       Needed because some endpoints report
 *                                       "nothing here" as an error — and the
 *                                       status is unreliable: utils/http.status.js
 *                                       exports only the strings Error/Success/
 *                                       Fail, so `AppError(msg, httpStatus.NOT_FOUND)`
 *                                       leaves statusCode undefined and the
 *                                       handler's `statusCode || 400` turns every
 *                                       such "not found" into a 400.
 * @param {any}    [options.emptyValue]  what to resolve to when isEmpty matches.
 */
export async function request(method, path, options = {}) {
  const { body, query, raw = false, isEmpty, emptyValue } = options;
  const verb = String(method).toUpperCase();

  let url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (query && Object.keys(query).length) {
    const qs = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    const s = qs.toString();
    if (s) url += `?${s}`;
  }

  const headers = { Accept: 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const init = { method: verb, headers };
  if (verb !== 'GET' && verb !== 'HEAD' && body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(url, init);
  } catch (networkError) {
    // fetch only rejects for network-level failures, so this is genuinely
    // "the API is unreachable" rather than an error response.
    throw new ApiError(
      'Cannot reach the server. Check that the API is running.',
      0,
      null,
    );
  }

  const contentType = res.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);

  if (!res.ok) {
    if (isEmpty && isEmpty(res.status, payload)) return emptyValue;
    const message = errorMessage(payload, `Request failed (HTTP ${res.status})`);
    // Only a genuinely bad session ends the session.
    if (res.status === 401 && !isRoleMismatch(message)) {
      unauthorizedHandlers.forEach((fn) => fn());
    }
    throw new ApiError(message, res.status, payload);
  }

  if (raw) return payload;
  // `data` is the envelope for every success response; a few endpoints nest a
  // second `data` inside it, which the api modules unwrap explicitly.
  return payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload;
}

export const get = (path, options) => request('GET', path, options);
export const post = (path, body, options) => request('POST', path, { ...options, body });
export const patch = (path, body, options) => request('PATCH', path, { ...options, body });
export const del = (path, options) => request('DELETE', path, options);

export { BASE_URL };
